import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { getPurchaseReconciliationQueue } from './api';
import { useOutlet } from './OutletContext';
import { useAuth } from './AuthContext';
import logger from './logger';

const ReconciliationBadgeContext = createContext(null);

export const useReconciliationBadge = () => {
  const ctx = useContext(ReconciliationBadgeContext);
  if (!ctx) {
    throw new Error('useReconciliationBadge must be used within ReconciliationBadgeProvider');
  }
  return ctx;
};

export const ReconciliationBadgeProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { selectedOutlet } = useOutlet();
  const [pendingCount, setPendingCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !selectedOutlet) {
      setPendingCount(0);
      return;
    }
    try {
      // PENDING tab request — server returns pending_count regardless of status filter
      const data = await getPurchaseReconciliationQueue(selectedOutlet, 'PENDING');
      setPendingCount(data?.pending_count || 0);
    } catch (e) {
      logger.info('[ReconciliationBadge] refresh failed (non-fatal):', e?.message);
      // Do NOT reset count on transient failure — keep last known good value
    }
  }, [isAuthenticated, selectedOutlet]);

  // Fetch on auth/outlet change (page load)
  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ pendingCount, refresh }), [pendingCount, refresh]);

  return (
    <ReconciliationBadgeContext.Provider value={value}>
      {children}
    </ReconciliationBadgeContext.Provider>
  );
};
